"use client";

import { useTranslations } from "next-intl";
import { Fragment } from "react";
import { SRCImage } from "react-datocms";

import { cn } from "@trip/utils";

import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { Large } from "~/components/typography";
import { buttonVariants } from "~/components/ui/button";
import { graphql, type FragmentOf } from "~/lib/dato-graphql";
import { responsiveImageFragment, tourPropsFragment } from "~/lib/fragments";
import { Link } from "~/navigation";

export const tourCardFragment = graphql(
  `
    fragment TourCardFragment on TourCardRecord @_unmask {
      __typename
      id
      description
      tour {
        heroImage {
          responsiveImage(
            imgixParams: { auto: format, fit: crop, w: 120, h: 120 }
          ) {
            ...ResponsiveImageFragment
          }
          alt
          title
        }
        ...TourPropsFragment
      }
    }
  `,
  [tourPropsFragment, responsiveImageFragment],
);

export function TourCard({
  description,
  tour,
}: FragmentOf<typeof tourCardFragment>) {
  const t = useTranslations("Article");

  return (
    <Fragment>
      <div className="text-japan mb-1.5 mt-4 text-center text-xs font-medium">
        {t("featuredTour")}
      </div>
      <Link
        prefetch={false}
        className="tour shadow-card-sm group mb-4 flex flex-col place-items-start items-stretch gap-6 rounded-3xl p-5 md:grid md:grid-cols-[1fr_1fr] md:items-center"
        href={`/tours/${tour.slug}/${tour.id}`}
      >
        <div>
          <SRCImage
            imgClassName="rounded-xl"
            data={tour.heroImage.responsiveImage}
          />
        </div>
        <div>
          <Large className="mb-2 line-clamp-2">{tour.title}</Large>
          {tour.tripadvisorRating ? (
            <div className="mb-2">
              <TripadvisorRating size="small" value={tour.tripadvisorRating} />
            </div>
          ) : null}
          <p className="text-sm">{description}</p>

          <div className="mt-2 flex items-center gap-4 sm:mt-4">
            <span
              className={cn(
                buttonVariants({ variant: "outline" }),
                "group-hover:bg-prune-200 whitespace-nowrap rounded-full",
              )}
            >
              {t("bookNow")}
            </span>
          </div>
        </div>
      </Link>
    </Fragment>
  );
}
